// Tool-call row for `role:'tool'` messages (the local-bridge transport pushes
// these) — same visual language as the ChatToolRow timeline: a quiet
// collapsed row (status glyph + tool name + duration-less hint), expandable to
// terminal-styled input/output blocks. Expansion rides root._aiToolOpen keyed
// by the message id, like the timeline rows, so it survives list re-patches.
// I/O renders through ToolTerminal (self-contained — resolves in the
// chrome-connect panel bundle where workspace/shared components don't).
export const ToolCallCard = {
  flow: 'y',
  width: '100%',
  minWidth: '0',

  Head: {
    tag: 'button',
    type: 'button',
    flow: 'x',
    gap: 'Z',
    align: 'center',
    width: '100%',
    minWidth: '0',
    padding: 'Y Z',
    round: 'Z',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'B defaultBezier background',
    ':hover': { background: 'hover' },
    onClick: (e, el, s) => {
      e.preventDefault()
      e.stopPropagation()
      const root = s.root
      if (!root || !root.update) return
      const open = { ...(root._aiToolOpen || {}) }
      open[s.id] = !open[s.id]
      root.update({ _aiToolOpen: open }, { preventFetch: true })
    },

    // Status glyph — a PERMANENT container so the glyph keeps its slot at the
    // row start; the variants inside are hide-gated (an `if`-gated child
    // REMOUNTS when status flips and lands appended at the row's end).
    // Key-based auto-extend: Icon / Icon_1 (the `_N` suffix is ignored).
    PvStatus: {
      boxSize: 'Z',
      flexShrink: '0',
      flow: 'x',
      align: 'center center',
      PvRunDot: {
        hide: (el, s) => s.status !== 'pending',
        boxSize: 'Y',
        round: '50%',
        background: 'blue',
        flexShrink: '0',
        animationName: 'blinking',
        animationDuration: '1100ms',
        animationTimingFunction: 'ease-in-out',
        animationIterationCount: 'infinite'
      },
      Icon: {
        hide: (el, s) => s.status === 'pending' || s.status === 'error',
        name: 'checkmark',
        boxSize: 'Z',
        color: 'green',
        flexShrink: '0'
      },
      Icon_1: {
        hide: (el, s) => s.status !== 'error',
        name: 'x',
        boxSize: 'Z',
        color: 'red',
        flexShrink: '0'
      }
    },

    Name: {
      tag: 'span',
      fontSize: 'Z1',
      fontWeight: '500',
      flexShrink: '0',
      color: (el, s) => (s.status === 'pending' ? 'title' : 'caption'),
      text: (el, s) => {
        const name = String(s.toolName || 'tool')
          .replace(/[_-]+/g, ' ')
          .trim()
        return name ? name[0].toUpperCase() + name.slice(1) : 'Tool'
      }
    },

    // Secondary meta: one line; the tooltip carries the arguments (capped)
    // — the overflow law's condition for an ellipsis (§3b rule 8). The card
    // itself expands to the full payload.
    Hint: {
      tag: 'span',
      fontSize: 'Z2',
      color: 'caption',
      opacity: '.65',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      flex: '1 1 auto',
      minWidth: '0',
      title: (el, s) => {
        const args = String(s.argsText || '')
          .replace(/\s+/g, ' ')
          .trim()
        if (!args) return null
        return args.length > 400 ? args.slice(0, 400) + '…' : args
      },
      text: (el, s) => {
        const args = String(s.argsText || '')
          .replace(/\s+/g, ' ')
          .trim()
        return args.length > 60 ? `${args.slice(0, 60)}…` : args
      }
    },

    Icon_2: {
      name: 'arrow angle down',
      boxSize: 'Z',
      color: 'caption',
      opacity: '.6',
      flexShrink: '0',
      transition: 'B defaultBezier transform',
      isOpen: (el, s) => !!(s.root && s.root._aiToolOpen && s.root._aiToolOpen[s.id]),
      '.isOpen': { transform: 'rotate(180deg)' }
    }
  },

  Detail: {
    hide: (el, s) => !(s.root && s.root._aiToolOpen && s.root._aiToolOpen[s.id]),
    flow: 'y',
    gap: 'Y',
    minWidth: '0',
    padding: 'X Z Y B',

    ToolTerminal: {
      if: (el, s) => !!s.argsText,
      label: 'ai.tool.input',
      value: (el, s) => s.argsText
    },
    ToolTerminal_1: {
      if: (el, s) => !!s.resultText,
      label: 'ai.tool.output',
      value: (el, s) => s.resultText
    }
  }
}
